import { useState, useCallback, useRef, useEffect } from "react";
import {
  VStack,
  HStack,
  FormControl,
  FormLabel,
  FormHelperText,
  FormErrorMessage,
  Input,
  Textarea,
  Button,
  Wrap,
  WrapItem,
  Tag,
  TagLabel,
  IconButton,
  Grid,
  Text,
  Icon,
} from "@chakra-ui/react";
import { Upload, X } from "lucide-react";
import { useThemeColors } from "@/hooks/useThemeColors";
import { apiClient } from "@/lib/api/client";
import { useToastNotification } from "@/lib/utils/useToastNotification";
import { sendCustomEmailSchema } from "@/lib/validations/auth";

const ALLOWED_EXTENSIONS = new Set([
  "pdf",
  "doc",
  "docx",
  "xls",
  "xlsx",
  "csv",
  "txt",
  "png",
  "jpg",
  "jpeg",
  "gif",
  "webp",
  "zip",
]);

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB
const MAX_ATTACHMENT_FILES = 5;
const ACCEPT_ATTR =
  ".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg,.gif,.webp,.zip,image/jpeg,image/png,image/gif,image/webp,application/pdf";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function parseEmails(input: string): string[] {
  return input
    .split(/[\s,;]+/)
    .map((e) => e.trim().toLowerCase())
    .filter((e) => e.length > 0);
}

function isValidEmail(email: string): boolean {
  return EMAIL_REGEX.test(email);
}

function getFileExtension(fileName: string): string {
  const idx = fileName.lastIndexOf(".");
  if (idx < 0 || idx === fileName.length - 1) return "";
  return fileName.slice(idx + 1).toLowerCase();
}

export interface SendCustomEmailFormProps {
  /** Shipping line name passed from parent (read-only context). */
  shippingLineName: string;
  onSuccess?: () => void;
  onCancel: () => void;
}

export function SendCustomEmailForm({
  shippingLineName,
  onCancel,
  onSuccess,
}: SendCustomEmailFormProps) {
  const colors = useThemeColors();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isMountedRef = useRef(true);
  const [email, setEmail] = useState("");
  const [ccInput, setCcInput] = useState("");
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [ccError, setCcError] = useState<string | null>(null);
  const [subjectError, setSubjectError] = useState<string | null>(null);
  const [bodyError, setBodyError] = useState<string | null>(null);
  const { showError, showSuccess } = useToastNotification();

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const validateAttachmentFile = useCallback((file: File): string | null => {
    const ext = getFileExtension(file.name);
    if (!ext || !ALLOWED_EXTENSIONS.has(ext)) {
      return `"${file.name}": allowed types are PDF, Word, Excel, CSV, TXT, images, or ZIP.`;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      return `"${file.name}": must be 5MB or less.`;
    }
    return null;
  }, []);

  const handleAttachmentChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setAttachmentError(null);
      const input = e.target;
      const fileList = input.files;
      if (!fileList || fileList.length === 0) return;

      const next: File[] = [];
      const errors: string[] = [];
      for (let i = 0; i < fileList.length; i++) {
        if (next.length >= MAX_ATTACHMENT_FILES) {
          errors.push(`Maximum ${MAX_ATTACHMENT_FILES} files allowed.`);
          break;
        }
        const file = fileList[i];
        const err = validateAttachmentFile(file);
        if (err) errors.push(err);
        else next.push(file);
      }
      if (errors.length > 0) {
        setAttachmentError(errors.slice(0, 3).join(" "));
      }
      setAttachmentFiles((prev) =>
        [...prev, ...next].slice(0, MAX_ATTACHMENT_FILES),
      );
      // Allow re-selecting the same file after remove
      input.value = "";
    },
    [validateAttachmentFile],
  );

  const removeAttachment = useCallback((index: number) => {
    setAttachmentFiles((prev) => prev.filter((_, i) => i !== index));
    setAttachmentError(null);
  }, []);

  const addCcEmail = useCallback(() => {
    setCcError(null);
    const parsed = parseEmails(ccInput);
    if (parsed.length === 0) return;
    const invalid = parsed.filter((e) => !isValidEmail(e));
    if (invalid.length > 0) {
      setCcError(`Invalid email(s): ${invalid.join(", ")}`);
      return;
    }
    setCcEmails((prev) => {
      const newEmails = parsed.filter((e) => !prev.includes(e));
      return [...prev, ...newEmails];
    });
    setCcInput("");
  }, [ccInput]);

  const removeCcEmail = useCallback((index: number) => {
    setCcEmails((prev) => prev.filter((_, i) => i !== index));
    setCcError(null);
  }, []);

  const handleCcKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" || e.key === "," || e.key === ";") {
        e.preventDefault();
        addCcEmail();
      }
    },
    [addCcEmail],
  );

  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return;

    setEmailError(null);
    setCcError(null);
    setSubjectError(null);
    setBodyError(null);

    const payload = {
      email: email.trim(),
      cc: ccEmails,
      subject: subject.trim(),
      body: body.trim(),
    };

    const parsed = sendCustomEmailSchema.safeParse(payload);
    if (!parsed.success) {
      const issues = parsed.error.flatten().fieldErrors;
      setEmailError(issues.email?.[0] ?? null);
      setCcError(issues.cc?.[0] ?? null);
      setSubjectError(issues.subject?.[0] ?? null);
      setBodyError(issues.body?.[0] ?? null);
      return;
    }

    if (attachmentFiles.length > MAX_ATTACHMENT_FILES) {
      setAttachmentError(`Maximum ${MAX_ATTACHMENT_FILES} files allowed.`);
      return;
    }

    const validData = parsed.data;
    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("email", validData.email);
      formData.append("subject", validData.subject);
      formData.append("body", validData.body);
      if (validData.cc && validData.cc.length > 0) {
        formData.append("cc", validData.cc.join(","));
      }
      attachmentFiles.forEach((file) => {
        formData.append("attachments[]", file, file.name);
      });

      await apiClient.post("/emails/send", formData, {
        timeout: 60000,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (!isMountedRef.current) return;
      showSuccess("Success", "Email sent successfully.");
      onSuccess?.();
    } catch (error: unknown) {
      if (!isMountedRef.current) return;
      showError(error as Error);
    } finally {
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }
    }
  }, [
    isSubmitting,
    email,
    ccEmails,
    subject,
    body,
    attachmentFiles,
    onSuccess,
    showError,
    showSuccess,
  ]);

  return (
    <VStack spacing={4} align="stretch">
      <Grid
        templateColumns={{ base: "1fr", md: "140px 1fr" }}
        gap={4}
        alignItems="center"
      >
        <FormLabel fontWeight="medium" color={colors.text} mb={0}>
          Shipping Line:
        </FormLabel>
        <Text
          color={colors.text}
          bg={colors.sectionBg}
          px={3}
          py={2}
          borderRadius="md"
          borderWidth="1px"
          borderColor={colors.border}
        >
          {shippingLineName || "-"}
        </Text>
      </Grid>

      <FormControl isInvalid={!!emailError} isRequired>
        <FormLabel fontWeight="medium" color={colors.text}>
          Email Address:
        </FormLabel>
        <Input
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setEmailError(null);
          }}
          placeholder="recipient@example.com"
          bg="white"
          borderColor={colors.border}
          color={colors.text}
          _placeholder={{ color: colors.textSecondary }}
        />
        {emailError && (
          <FormHelperText color="red.500" mt={1}>
            {emailError}
          </FormHelperText>
        )}
      </FormControl>

      <FormControl isInvalid={!!ccError}>
        <FormLabel fontWeight="medium" color={colors.text}>
          CC:
        </FormLabel>
        <Input
          type="text"
          value={ccInput}
          onChange={(e) => {
            setCcInput(e.target.value);
            setCcError(null);
          }}
          onKeyDown={handleCcKeyDown}
          onBlur={addCcEmail}
          placeholder="Add CC emails (input comma or press Enter in keyboard to add)"
          bg="white"
          borderColor={colors.border}
          color={colors.text}
          _placeholder={{ color: colors.textSecondary }}
        />
        {ccEmails.length > 0 && (
          <Wrap spacing={2} mt={2}>
            {ccEmails.map((addr, index) => (
              <WrapItem key={`${addr}-${index}`}>
                <Tag
                  size="md"
                  colorScheme="gray"
                  borderRadius="full"
                  gap={1}
                  variant="subtle"
                >
                  <TagLabel maxW="180px" noOfLines={1} title={addr}>
                    {addr}
                  </TagLabel>
                  <IconButton
                    aria-label={`Remove ${addr}`}
                    size="xs"
                    variant="ghost"
                    icon={<Icon as={X} boxSize={3} />}
                    onClick={() => removeCcEmail(index)}
                    minW={6}
                    minH={6}
                  />
                </Tag>
              </WrapItem>
            ))}
          </Wrap>
        )}
        {ccError && (
          <FormHelperText color="red.500" mt={1}>
            {ccError}
          </FormHelperText>
        )}
      </FormControl>

      <FormControl isInvalid={!!subjectError} isRequired>
        <FormLabel fontWeight="medium" color={colors.text}>
          Subject:
        </FormLabel>
        <Input
          value={subject}
          onChange={(e) => {
            setSubject(e.target.value);
            setSubjectError(null);
          }}
          placeholder="Subject"
          bg="white"
          borderColor={colors.border}
          color={colors.text}
          _placeholder={{ color: colors.textSecondary }}
        />
        {subjectError && (
          <FormHelperText color="red.500" mt={1}>
            {subjectError}
          </FormHelperText>
        )}
      </FormControl>

      <FormControl isInvalid={!!bodyError} isRequired>
        <FormLabel fontWeight="medium" color={colors.text}>
          Body:
        </FormLabel>
        <Textarea
          value={body}
          onChange={(e) => {
            setBody(e.target.value);
            setBodyError(null);
          }}
          placeholder="Body"
          rows={6}
          bg="white"
          borderColor={colors.border}
          color={colors.text}
          _placeholder={{ color: colors.textSecondary }}
        />
        {bodyError && (
          <FormHelperText color="red.500" mt={1}>
            {bodyError}
          </FormHelperText>
        )}
      </FormControl>

      <FormControl isInvalid={!!attachmentError}>
        <FormLabel fontWeight="medium" color={colors.text}>
          Attachments
        </FormLabel>
        <FormHelperText color={colors.textSecondary} mt={0}>
          PDF, Word, Excel, CSV, TXT, images, or ZIP. Max 5MB each. Max{" "}
          {MAX_ATTACHMENT_FILES} files.
        </FormHelperText>
        <VStack align="stretch" spacing={3} mt={2}>
          <HStack spacing={2} flexWrap="wrap">
            <Button
              size="sm"
              leftIcon={<Icon as={Upload} boxSize={4} />}
              onClick={() => fileInputRef.current?.click()}
              isDisabled={
                isSubmitting || attachmentFiles.length >= MAX_ATTACHMENT_FILES
              }
              variant="outline"
              borderColor={colors.border}
              color={colors.text}
            >
              Choose files
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT_ATTR}
              multiple
              onChange={handleAttachmentChange}
              style={{ display: "none" }}
              aria-hidden
            />
            {attachmentFiles.length > 0 && (
              <Text fontSize="sm" color={colors.textSecondary}>
                {attachmentFiles.length} file(s) selected
              </Text>
            )}
          </HStack>
          <FormErrorMessage>{attachmentError ?? ""}</FormErrorMessage>
          {attachmentFiles.length > 0 && (
            <Wrap spacing={2}>
              {attachmentFiles.map((file, index) => (
                <WrapItem key={`${file.name}-${file.size}-${index}`}>
                  <Tag size="md" colorScheme="blue" borderRadius="full" gap={1}>
                    <TagLabel maxW="120px" noOfLines={1} title={file.name}>
                      {file.name}
                    </TagLabel>
                    <IconButton
                      aria-label={`Remove ${file.name}`}
                      size="xs"
                      variant="ghost"
                      icon={<Icon as={X} boxSize={3} />}
                      onClick={() => removeAttachment(index)}
                      isDisabled={isSubmitting}
                      minW={6}
                      minH={6}
                    />
                  </Tag>
                </WrapItem>
              ))}
            </Wrap>
          )}
        </VStack>
      </FormControl>

      <HStack spacing={4} justify="flex-end" pt={2} w="full">
        <Button
          variant="outline"
          onClick={onCancel}
          isDisabled={isSubmitting}
          borderColor={colors.border}
          color={colors.text}
        >
          Cancel
        </Button>
        <Button
          colorScheme="blue"
          onClick={handleSubmit}
          isLoading={isSubmitting}
          loadingText="Sending..."
        >
          Send Email
        </Button>
      </HStack>
    </VStack>
  );
}
